import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { money } from "@/dealership/lib/api";
import PageHeader from "@/dealership/components/PageHeader";
import PriceAlertsBell from "@/dealership/components/PriceAlertsBell";
import { StatTile, NeedsAttention, Panel, LivePill, EmptyState } from "@/components/lux";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Users, Car, DollarSign, TrendingUp, Receipt, ShieldCheck, Percent, Warehouse, BellRing,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";

const SLICE_COLORS = ["#d6bb84", "#4ade80", "#38bdf8", "#a78bfa", "#fb923c", "#f87171"];

const TOOLTIP_STYLE = {
  contentStyle: { background: "#121216", border: "1px solid #24242a", borderRadius: 12, fontSize: 12 },
  labelStyle: { color: "#a1a1aa" },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });

  if (isLoading || !data) {
    return (
      <div className="p-6 sm:p-8">
        <PageHeader title="Dashboard" subtitle="Loading your dealership snapshot…" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-36 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  const k = data.kpis;
  const fu = data.followups || { overdue: 0, today: 0, items: [] };

  const attention = [
    { key: "overdue", label: "Follow-ups overdue", count: fu.overdue || 0, tone: "red", to: "/dealership/activities" },
    { key: "today", label: "Follow-ups due today", count: fu.today || 0, tone: "amber", to: "/dealership/activities" },
    { key: "leads", label: "Leads in the pipeline", count: k.total_leads || 0, tone: "blue", to: "/dealership/leads" },
    { key: "stock", label: "Units in stock", count: k.total_inventory || 0, tone: "gold", to: "/dealership/inventory" },
  ];

  return (
    <div className="p-6 sm:p-8" data-testid="dealer-dashboard-page">
      <PageHeader
        title="Dealership"
        subtitle={`${k.total_inventory} units in stock · ${k.total_leads} leads · ${k.conversion_rate}% conversion`}
        live={<LivePill />}
        action={<PriceAlertsBell />}
      />

      {(fu.overdue > 0 || fu.today > 0) && (
        <div
          className="w-full mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-4 flex items-center justify-between gap-4 hover:bg-amber-500/[0.1] transition-colors text-left cursor-pointer"
          onClick={() => navigate("/dealership/activities")}
          data-testid="dashboard-followups-banner"
        >
          <div className="flex items-center gap-3">
            <BellRing className="h-5 w-5 text-amber-400 shrink-0" />
            <div className="text-sm">
              <span className="font-medium text-amber-300">
                {fu.overdue > 0 && `${fu.overdue} overdue`}
                {fu.overdue > 0 && fu.today > 0 && " · "}
                {fu.today > 0 && `${fu.today} due today`}
              </span>
              <span className="text-muted-foreground"> — follow-ups need your attention</span>
            </div>
          </div>
          <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/15">View activities</Button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4 stagger">
        <StatTile label="Total Revenue" value={money(k.total_revenue)} gold hint="Finalized deals, all time" icon={DollarSign} tone="gold" testid="kpi-revenue" />
        <StatTile label="Gross Profit" value={money(k.total_profit)} gold hint="After cost of vehicle & recon" icon={TrendingUp} tone="green" testid="kpi-profit" />
        <StatTile label="Inventory Value" value={money(k.inventory_value)} gold hint={`${k.total_inventory} units on the lot`} icon={Warehouse} tone="gold" testid="kpi-inventory-value" />
        <StatTile label="HST Collected" value={money(k.hst_collected)} gold hint="Remittable tax on closed deals" icon={Receipt} tone="blue" testid="kpi-hst" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 stagger">
        <StatTile label="Total Leads" value={k.total_leads} hint="All sources" icon={Users} tone="blue" testid="kpi-leads" />
        <StatTile label="Units In Stock" value={k.total_inventory} hint="Available for sale" icon={Car} tone="green" testid="kpi-units" />
        <StatTile label="Warranty Revenue" value={money(k.warranty_revenue)} gold hint="Back-end product income" icon={ShieldCheck} tone="gold" testid="kpi-warranty" />
        <StatTile label="Lead Conversion" value={`${k.conversion_rate}%`} hint="Leads that became deals" icon={Percent} tone="amber" testid="kpi-conversion" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel label="Performance" title="Monthly revenue & profit" className="lg:col-span-2">
          {data.monthly.length === 0 ? (
            <EmptyState icon={TrendingUp} title="No finalized deals yet" hint="Close a deal to start tracking monthly performance." />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="month" fontSize={11} stroke="#71717A" tickLine={false} axisLine={false} />
                <YAxis fontSize={11} stroke="#71717A" tickLine={false} axisLine={false} />
                <Tooltip formatter={(v) => money(v)} {...TOOLTIP_STYLE} />
                <Bar dataKey="revenue" fill="#d6bb84" radius={[6, 6, 0, 0]} name="Revenue" maxBarSize={38} />
                <Bar dataKey="profit" fill="#4ade80" radius={[6, 6, 0, 0]} name="Profit" maxBarSize={38} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <NeedsAttention items={attention} testid="dealer-needs-attention" />
      </div>

      <div className="mt-4">
        <Panel label="Pipeline" title="Leads by status">
          {data.lead_by_status.length === 0 ? (
            <EmptyState icon={Users} title="No leads yet" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.lead_by_status} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={90} label={(e) => e.status} fontSize={11} stroke="#121216">
                  {data.lead_by_status.map((_, i) => <Cell key={i} fill={SLICE_COLORS[i % SLICE_COLORS.length]} />)}
                </Pie>
                <Tooltip {...TOOLTIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>
    </div>
  );
}
